import { motion } from "framer-motion";

const steps = [
    {
        id: 1,
        title: "Pilih Layanan Poli",
        description: "Pilih layanan poli sesuai kebutuhan medis Anda.",
        icon: "/Icons/card1-careline.svg",
    },
    {
        id: 2,
        title: "Pilih Dokter",
        description: "Tentukan dokter yang sesuai dengan kebutuhan Anda.",
        icon: "/Icons/card2-careline.svg",
    },
    {
        id: 3,
        title: "Ambil Nomor Antrian",
        description:
            "Dapatkan nomor antrian langsung secara digital tanpa perlu mengantri.",
        icon: "/Icons/card3-careline.svg",
    },
    {
        id: 4,
        title: "Tunggu dan Diperiksa",
        description: "Pantau nomor antrian Anda dan tunggu giliran.",
        icon: "/Icons/card4-careline.svg",
    },
];

const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.6,
            delay: i * 0.12,
            ease: [0.25, 0.46, 0.45, 0.94],
        },
    }),
};

function CareLineSteps() {
    return (
        <section className="relative overflow-hidden py-16 md:py-20 font-[Plus_Jakarta_Sans,sans-serif]">
            <div className="absolute -top-36 -right-24 w-[500px] h-[500px] rounded-full bg-cyan-500/5 blur-[80px] pointer-events-none z-0" />
            <div className="absolute -bottom-20 -left-14 w-[300px] h-[300px] rounded-full bg-cyan-500/[0.04] blur-[80px] pointer-events-none z-0" />

            <div className="relative z-10 mx-auto max-w-[1280px] px-4 sm:px-6 md:px-10 lg:px-16 xl:px-[60px]">
                <motion.div
                    className="flex flex-col items-center text-center mb-10 md:mb-14"
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{
                        duration: 0.7,
                        ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                >
                    <div className="inline-flex items-center gap-2 bg-cyan-50 border border-cyan-200/70 text-cyan-600 text-[11px] font-bold px-3.5 py-1.5 rounded-full uppercase tracking-[0.08em] mb-5">
                        <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full" />
                        Panduan Antrian
                    </div>
                    <h2 className="text-[clamp(24px,3.5vw,40px)] font-extrabold text-gray-900 leading-tight tracking-[-0.02em] mb-3">
                        Cara Mengambil{" "}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#06B6D4] to-[#0891B2]">
                            Antrian
                        </span>
                    </h2>
                    <p className="text-gray-400 text-[15px] max-w-sm leading-relaxed font-medium">
                        Ikuti langkah-langkah berikut untuk mendapatkan nomor
                        antrian dengan mudah.
                    </p>
                </motion.div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 items-stretch">
                    {steps.map((step, idx) => (
                        <motion.div
                            key={step.id}
                            className="relative"
                            custom={idx}
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.2 }}
                        >
                            {idx < steps.length - 1 && (
                                <div className="hidden lg:flex absolute top-11 -right-4 z-10 items-center">
                                    <svg
                                        width="32"
                                        height="14"
                                        viewBox="0 0 32 14"
                                        fill="none"
                                        stroke="#a5f3fc"
                                        strokeWidth="1.5"
                                        strokeDasharray="4 3"
                                    >
                                        <path d="M0 7 H26 M22 3 L28 7 L22 11" />
                                    </svg>
                                </div>
                            )}
                            <motion.div
                                className="group relative flex flex-col h-full bg-white rounded-[20px] px-6 pt-7 pb-6 border border-[#e8f4f8] overflow-hidden"
                                whileHover={{
                                    y: -6,
                                    boxShadow:
                                        "0 8px 16px rgba(0,0,0,0.05), 0 24px 48px rgba(6,182,212,0.10)",
                                    borderColor: "rgba(6,182,212,0.2)",
                                }}
                                transition={{ duration: 0.25 }}
                            >
                                <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#06B6D4] to-[#67e8f9] rounded-t-[20px] scale-x-0 origin-left transition-transform duration-[350ms] group-hover:scale-x-100" />
                                <div className="flex items-start justify-between mb-6">
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#06B6D4] to-[#0891B2]">
                                        <img
                                            src={step.icon}
                                            alt={step.title}
                                            className="w-6 h-6 brightness-0 invert"
                                        />
                                    </div>
                                    <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-[#06B6D4] to-[#0891B2] text-white text-[13px] font-extrabold">
                                        {String(step.id).padStart(2, "0")}
                                    </div>
                                </div>
                                <div className="w-full h-px bg-gradient-to-r from-cyan-100 to-transparent mb-5" />
                                <div>
                                    <h3 className="text-[15px] font-bold text-gray-900 mb-2 leading-snug tracking-[-0.01em]">
                                        {step.title}
                                    </h3>
                                    <p className="text-gray-400 text-[13px] leading-relaxed font-medium">
                                        {step.description}
                                    </p>
                                </div>
                                <div className="mt-auto pt-5 flex items-center gap-1.5">
                                    {steps.map((_, i) => (
                                        <div
                                            key={i}
                                            className={`h-[3px] rounded-full transition-all duration-300 ${i === idx ? "w-5 bg-[#06B6D4]" : i < idx ? "w-2 bg-cyan-200" : "w-2 bg-gray-100"}`}
                                        />
                                    ))}
                                </div>
                            </motion.div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default CareLineSteps;